
languageService.$inject = ['system-config.constant', 'googleApiProvider'];

export function languageService(systemConfig, googleApiProvider) {
  'use strict';

  let initialLanguage = '';
  const loadedLanguageGoogleMaps = {};

  function extractPrimaryLanguage(fullLanguage) {
    return fullLanguage
      ? fullLanguage.split('-')[0].toLowerCase()
      : fullLanguage;
  }

  /**
   * Returns the best match of a locale that AngularJS supports
   */
  function toAngularLocale(languageTag) {
    // return AngularJS locale tag, provided in a separate locale file that can be loaded

    let bestMatchLocale = languageTag.toLowerCase();

    if (!systemConfig.supportedAngularLocales.includes(bestMatchLocale)) {
      bestMatchLocale = extractPrimaryLanguage(bestMatchLocale);

      if (bestMatchLocale === 'en' || !systemConfig.supportedAngularLocales.includes(bestMatchLocale)) {
        bestMatchLocale = systemConfig.fallbackLocaleAngular;
      }
    }

    return bestMatchLocale;
  }

  /**
   * Returns the closest match of locales that jQuery UI's date picker supports
   */
  function toJQueryUiDatePickerLocale(languageTag) {
    // Since all supported locales are loaded into the global array $.datepicker.regional,
    // we can easily determine a best match
    let datePickerLang = languageTag;

    if (!(datePickerLang in $.datepicker.regional)) {
      // Language + country not available, try with language
      datePickerLang = extractPrimaryLanguage(languageTag);

      // Fix for English date formatting in calendar entry details view
      // Date needs to be formatted as dd/mm (UK) instead of mm/dd (US)
      if (datePickerLang === 'en') {
        datePickerLang = 'en-GB';
      }

      if (!(datePickerLang in $.datepicker.regional)) {
        // Language also not supported, fallback to date picker's default
        // Please note, that this also happens for 'en' which is the default
        datePickerLang = '';
      }
    }
    return datePickerLang;
  }

  /*
	 * Public functions of provider
	 */

  this.toAngularLocale = function(languageTag) {
    return toAngularLocale(languageTag);
  };

  this.toJQueryUiDatePickerLocale = function(languageTag) {
    return toJQueryUiDatePickerLocale(languageTag);
  };

  // Set language on application startup
  this.setInitialLanguage = function(languageTag) {
    initialLanguage = languageTag;

    loadGoogleMapForLanguage(initialLanguage);
  };

  // TODO: refactor `loadGoogleMapForLanguage`
  function loadGoogleMapForLanguage(locale) {
    if (!window.googleApiKey) return;

    // FIXME: `loadedLanguageGoogleMaps[locale]` is not set. this means checks are superfluous
    if (loadedLanguageGoogleMaps[locale]) {
      // already loaded
      window.google.maps = loadedLanguageGoogleMaps[locale];
    } else {
      // load first time and keep copy

      // remove previously loaded language google maps
      if (window.google && window.google.maps) delete window.google.maps;

      // load google maps with locale and dist/dev `googleApiKey`
     googleApiProvider.init(locale).then(function() {
       loadedLanguageGoogleMaps[locale] = window.google.maps;
     });
    }
  }

  /*
	 * Service
	 */
  this.$get = [
    '$q',
    '$window',
    'i18nFilter',
    'system-config.constant',
    function(
        $q,
        $window,
        i18nFilter,
        systemConfig
    ) {
      // Use language from provider
      var languageTag = initialLanguage;

      function isSupportedPrimaryLanguage(primaryLanguage) {
        if (primaryLanguage.indexOf('-') > -1) {
          return (
            systemConfig.supportedLanguages.indexOf(
              primaryLanguage.split('-')[0]
            ) > -1
          );
        }
        return systemConfig.supportedLanguages.indexOf(primaryLanguage) > -1;
      }

      function getLanguage() {
        return languageTag;
      }

      return {
        /**
         * Returns an array of supported primary languages like 'de'
         */
        getSupportedPrimaryLanguages: function() {
          return systemConfig.supportedLanguages;
        },

        /**
         * Returns a map of isoCode -> translated language name { 'de': 'Deutsch', ... }
         * It can be used in ngOptions, e.g. like this:
         * ng-options="languageTag as value for (languageTag, value) in ctrl.languages | orderBy : 'languageTag'"
         */
        getSupportedPrimaryLanguagesMap: function() {
          return systemConfig.supportedLanguages.reduce(function(
            languageMap,
            languageTag
          ) {
            languageMap[languageTag] = i18nFilter(
              'fe.admin.settings.uiLanguage.language.' + languageTag,
              languageTag
            );
            return languageMap;
          },
          {});
        },

        getSupportedPrimaryLanguagesNativeArray: function() {
          return [
            // { key: 'bg', value: 'български' },
            { key: 'de', value: 'Deutsch' },
            { key: 'en', value: 'English' },
            { key: 'fr', value: 'Français' },
            { key: 'pl', value: 'Polski' }
          ];
        },

        getSupportedPrimaryLanguagesNativeMap: function() {
          return {
            de: 'Deutsch',
            en: 'English',
            pl: 'Polski',
            fr: 'Français',
            // bg: 'български'
          };
        },

        /**
         * Returns the IETF language tag (RFC 5646), e.g. 'de-DE'
         */
        getLanguage: getLanguage,

        /**
         * Sets the IETF language tag (RFC 5646), e.g. 'de-DE'
         *
         * Returns a promise that is resolved when everything has been loaded or rejected if the
         * language is not supported
         */
        setLanguage: function(newLanguage, isSelfRegistration) {
          var newPrimaryLanguage = extractPrimaryLanguage(newLanguage);

          console.debug('setLanguage(' + newLanguage + ')');

          // do *not* load google maps on self registration site
          // https://365farmnet.atlassian.net/browse/CFNPOC-27336
          if (!isSelfRegistration) loadGoogleMapForLanguage(newLanguage);

          if (!isSupportedPrimaryLanguage(newPrimaryLanguage)) {
            return $q.reject(
              'Primary language not supported: ' + newPrimaryLanguage
            );
          } else if (newLanguage != languageTag) {
            languageTag = newLanguage;

            if (!isSelfRegistration) {
              $window.location.reload();
            }
            return $q.when();
          } else {
            // We already have the correct language
            return $q.when();
          }
        },

        /**
         * Sets the primary language without losing the region. So setting 'de' if the locale is 'de-DE'
         * will keep it 'de-DE' while setting to 'en' will set to 'en'.
         *
         * Returns a promise that is resolved when everything is set and loaded
         */
        setPrimaryLanguage: function(newPrimaryLanguage) {
          if (newPrimaryLanguage != this.getPrimaryLanguage()) {
            return this.setLanguage(newPrimaryLanguage);
          } else {
            return $q.when();
          }
        },

        /**
         * Returns the primary language, e.g. "de" if language tag is "de-DE"
         */
        getPrimaryLanguage: function() {
          var lang = systemConfig.fallbackLocale;

          if (isSupportedPrimaryLanguage(this.getLanguage())) {
            lang = extractPrimaryLanguage(this.getLanguage());
          }

          return lang;
        },

        /**
         * Guesses the country by language. Returns a ISO 3166-1 alpha-3 code.
         */
        guessCountry: function() {
          // if we cannot guess a country, we simply use "unknown" which i.E. causes a controller to prepend
          // an item to a list or array that might read 'please select a country' later since we cannot identify a proper country
          // and pre-select it through here, but badly want a user to take actions on a <select> or whatever else
          var country = 'UKN';

          /*
					 * "ISO 639-1" or "ISO 3166-1 alpha-2" to "ISO 3166-1 alpha-3" mapping
					 *
					 * The list below consists of 1:n and 1:1 (europe) relations between a language and a country
					 * If the locale is representing 1:n (like German for example) relation we cannot simply say
					 * it have to be Germany then due to the fact, that Austrians and Swiss folks are also speaking German.
					 */
          var localeToCountryMap = {
            pl: 'POL',
            'pl-pl': 'POL',
            //'en' : 'GBR', there is more than one country in europe where English is officialese
            'en-gb': 'GBR',
            de: 'DEU',
            'de-de': 'DEU',
            'de-ch': 'CHE',
            'de-at': 'AUT',
            //'fr' : 'FRA', there is more than one country in europe where French is officialese
            'fr-fr': 'FRA',
            'fr-be': 'BEL',
            'nl-be': 'BEL',
            'fr-ch': 'CHE',
            bg: 'BGR',
            'bg-bg': 'BGR',
            da: 'DNK',
            'da-dk': 'DNK',
            et: 'EST',
            'et-ee': 'EST',
            ee: 'EST',
            fi: 'FIN',
            'fi-fi': 'FIN',
            gr: 'GRC',
            'el-gr': 'GRC',
            el: 'GRC',
            ga: 'IRL',
            'ga-ie': 'IRL',
            'en-ie': 'IRL',
            it: 'ITA',
            'it-it': 'ITA',
            hr: 'HRV',
            'hr-hr': 'HRV',
            lv: 'LVA',
            'lv-lv': 'LVA',
            lt: 'LTU',
            'lt-lt': 'LTU',
            'lb-lu': 'LUX',
            'mt-mt': 'MLT',
            nl: 'NLD',
            'nl-nl': 'NLD',
            pt: 'PRT',
            'pt-pt': 'PRT',
            ro: 'ROU',
            'ro-ro': 'ROU',
            se: 'SWE',
            'sv-se': 'SWE',
            'se-se': 'SWE',
            sk: 'SVK',
            'sk-sk': 'SVK',
            si: 'SVN',
            'si-lk': 'SVN',
            sl: 'SVN',
            es: 'ESP',
            'es-es': 'ESP',
            cs: 'CZE',
            'cs-cz': 'CZE',
            hu: 'HUN',
            'hu-hu': 'HUN',
            cy: 'CYP'
          };

          var theLocale = languageTag.toLowerCase();
          if (theLocale)
            if (localeToCountryMap[theLocale]) {
              country = localeToCountryMap[theLocale];
            }

          console.debug(
            'Language tag "' +
              this.getLanguage() +
              '", guessed country: ' +
              country
          );

          return country;
        },

        getDatePickerConfig: function() {
          var datePickerLang = toJQueryUiDatePickerLocale(this.getLanguage()),
            datePickerConfig = $.datepicker.regional[datePickerLang];

          console.debug(
            'Returning DatePicker config for: "' + datePickerLang + '"'
          );

          // Heads up: If date picker config is changed here, it will have no effect on initial
          // locale load, which takes place during app.config phase and does not use this
          // function
          return datePickerConfig;
        }
      };
    }
  ];
}
